<script setup lang="ts">
import { type Author } from "@/services/fobizzToolsApi";
import { type UserUnion } from "@/services/orval/internalApiV1";
import { isPupil, isUser } from "@/stores/user";
import { Subscriber } from "@/types";

const props = withDefaults(
  defineProps<{
    user?: UserUnion | Subscriber | Author;
    text?: string;
    size?: "sm";
    background?: "gray";
    border?: boolean;
    shadow?: boolean;
  }>(),
  {
    user: undefined,
    text: undefined,
    size: undefined,
    background: undefined,
    border: false,
    shadow: false,
  }
);

const avatarInitials = computed(() => {
  if (isPupil(props.user)) {
    return "";
  } else if (isUser(props.user)) {
    return props.user.initials;
  } else {
    return "?";
  }
});

const resolveTitle = computed(() => {
  if (isPupil(props.user)) {
    return props.user.name;
  } else if (isUser(props.user)) {
    return props.user.publicDisplayName;
  }

  return "";
});

const cssClasses = computed(() => {
  return {
    "avatar2--sm": props.size === "sm",
    "avatar2--shadow": props.shadow,
  };
});

const cssClassesImage = computed(() => {
  return {
    "avatar2__image--border": props.border,
  };
});

const cssClassesMissing = computed(() => {
  return {
    "avatar2__missing--bg-gray": props.background === "gray",
    "avatar2__missing--border": props.border,
  };
});
</script>

<template>
  <div :title="resolveTitle" class="avatar2" :class="cssClasses">
    <template v-if="user">
      <img
        v-if="isUser(user)"
        :src="user.avatarUrl"
        class="avatar2__image"
        :class="cssClassesImage"
        :alt="avatarInitials"
      />
      <div v-else class="avatar2__missing" :class="cssClassesMissing">
        {{ avatarInitials }}
      </div>
    </template>
    <div v-else class="avatar2__missing" :class="cssClassesMissing">
      {{ text }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.avatar2 {
  width: 38px;
  height: 38px;
  display: flex;
  border-radius: 50%;

  &--sm {
    width: 32px;
    height: 32px;
  }

  &--shadow {
    box-shadow: $brand-box-shadow;
  }

  &__image {
    width: 100%;
    height: auto;
    object-fit: cover;
    border-radius: 50%;

    &--border {
      border: 1.5px solid $border-color;
    }
  }

  &__missing {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $brand-tools-light;
    color: $brand-tools;
    border-radius: 50%;
    font-size: $font-size-xs;
    font-weight: $font-weight-semibold;
    text-transform: uppercase;

    &--bg-gray {
      background-color: $gray-200;
      border-color: $border-color;
      color: $body-color;
    }

    &--border {
      border: 1.5px solid $color-white;
    }
  }
}
</style>
